import React, { useMemo, useState } from 'react';
import { feeForProcedure } from '../../utils/dentalFees';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { escapeHtml } from '../../utils/escapeHtml';

/** FDI adult dental chart for Dental OP workspace */
const UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];
const SURFACES = [
  { id: 'M', label: 'Mesial' },
  { id: 'D', label: 'Distal' },
  { id: 'B', label: 'Buccal / Labial' },
  { id: 'L', label: 'Lingual / Palatal' },
  { id: 'O', label: 'Occlusal / Incisal' },
];
const CONDITIONS = [
  'Healthy', 'Caries', 'Filled', 'Missing', 'Crown', 'RCT',
  'Extraction needed', 'Implant', 'Bridge', 'Fracture', 'Mobility',
];
const PROCEDURES = [
  'Oral examination', 'Scaling & polishing', 'Root canal treatment (RCT)',
  'Composite filling', 'Amalgam filling', 'Tooth extraction',
  'Crown preparation', 'Crown cementation', 'Implant placement',
  'Periodontal therapy', 'Orthodontic adjustment', 'Pulp capping',
  'Pulpectomy', 'Space maintainer', 'Denture adjustment',
];
const COLOR = {
  Healthy: '#e2e8f0', Caries: '#fca5a5', Filled: '#93c5fd', Missing: '#cbd5e1',
  Crown: '#fcd34d', RCT: '#c4b5fd', 'Extraction needed': '#f87171',
  Implant: '#6ee7b7', Bridge: '#fdba74', Fracture: '#fb7185', Mobility: '#a5b4fc',
};

const PLAN_STATUSES = ['Planned', 'Accepted', 'Completed', 'Declined'];

function normalizeChart(value) {
  if (!value || typeof value !== 'object') {
    return { dentition: 'adult', teeth: {}, findings: [], procedures: [], treatmentPlan: [] };
  }
  if (value.teeth || value.findings || value.procedures || value.treatmentPlan) {
    return {
      dentition: value.dentition || 'adult',
      teeth: value.teeth || {},
      findings: Array.isArray(value.findings) ? value.findings : [],
      procedures: Array.isArray(value.procedures) ? value.procedures : [],
      treatmentPlan: Array.isArray(value.treatmentPlan) ? value.treatmentPlan : [],
    };
  }
  // Legacy map { [tooth]: { condition, note } }
  return {
    dentition: 'adult',
    teeth: value,
    findings: [],
    procedures: [],
    treatmentPlan: [],
  };
}

export default function DentalChart({ value = {}, onChange, readOnly = false }) {
  const { hospital } = useHospitalProfile();
  const hospitalName = hospital?.name || '';
  const chart = useMemo(() => normalizeChart(value), [value]);
  const [multi, setMulti] = useState(false);
  const [selected, setSelected] = useState([]);
  const [surfaces, setSurfaces] = useState([]);
  const [findingText, setFindingText] = useState('');
  const [findingDate, setFindingDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [procForm, setProcForm] = useState({
    name: '', tooth: '', surfaces: [], visits: '1', date: new Date().toISOString().slice(0, 10), notes: '', fee: '',
  });
  const [planForm, setPlanForm] = useState({
    name: '', teeth: '', fee: '', visits: '1', status: 'Planned', notes: '',
  });

  const planTotals = useMemo(() => {
    const plan = chart.treatmentPlan || [];
    const sumBy = (status) => plan
      .filter((p) => p.status === status)
      .reduce((acc, p) => acc + (Number(p.fee) || 0), 0);
    return {
      planned: sumBy('Planned'),
      accepted: sumBy('Accepted'),
      completed: sumBy('Completed'),
      grand: plan.reduce((acc, p) => acc + (Number(p.fee) || 0), 0),
    };
  }, [chart.treatmentPlan]);

  const commit = (next) => {
    if (readOnly || !onChange) return;
    onChange(next);
  };

  const toggleTooth = (num) => {
    if (readOnly) return;
    setSelected((prev) => {
      if (multi) {
        return prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num];
      }
      return prev[0] === num ? [] : [num];
    });
  };

  const toggleSurface = (id) => {
    setSurfaces((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const setToothCondition = (condition) => {
    if (!selected.length) return;
    const teeth = { ...chart.teeth };
    selected.forEach((num) => {
      teeth[num] = {
        ...(teeth[num] || {}),
        condition,
        surfaces: surfaces.length ? [...surfaces] : (teeth[num]?.surfaces || []),
        note: teeth[num]?.note || '',
      };
    });
    commit({ ...chart, teeth });
  };

  const addFinding = () => {
    if (!selected.length || !findingText.trim()) return;
    const row = {
      id: `F-${Date.now()}`,
      teeth: [...selected],
      surfaces: [...surfaces],
      text: findingText.trim(),
      date: findingDate,
    };
    commit({ ...chart, findings: [...chart.findings, row] });
    setFindingText('');
  };

  const addProcedure = () => {
    if (!procForm.name.trim()) return;
    const toothList = procForm.tooth
      ? procForm.tooth.split(/[,\s]+/).map((t) => t.trim()).filter(Boolean)
      : [...selected];
    const row = {
      id: `P-${Date.now()}`,
      name: procForm.name.trim(),
      teeth: toothList,
      surfaces: procForm.surfaces.length ? [...procForm.surfaces] : [...surfaces],
      visits: Number(procForm.visits) || 1,
      date: procForm.date,
      notes: procForm.notes.trim(),
      fee: Number(procForm.fee) || feeForProcedure(procForm.name),
    };
    commit({ ...chart, procedures: [...chart.procedures, row] });
    setProcForm({
      name: '', tooth: '', surfaces: [], visits: '1', date: new Date().toISOString().slice(0, 10), notes: '', fee: '',
    });
  };

  const addPlanItem = (seed = {}) => {
    const name = (seed.name || planForm.name).trim();
    if (!name) return;
    const teeth = seed.teeth
      || (planForm.teeth ? planForm.teeth.split(/[,\s]+/).map((t) => t.trim()).filter(Boolean) : [...selected]);
    const row = {
      id: `TP-${Date.now()}`,
      name,
      teeth: Array.isArray(teeth) ? teeth : [],
      fee: Number(seed.fee ?? planForm.fee) || feeForProcedure(name),
      visits: Number(seed.visits ?? planForm.visits) || 1,
      status: seed.status || planForm.status || 'Planned',
      notes: (seed.notes ?? planForm.notes).trim(),
      procedureId: seed.procedureId || '',
    };
    commit({ ...chart, treatmentPlan: [...(chart.treatmentPlan || []), row] });
    setPlanForm({ name: '', teeth: '', fee: '', visits: '1', status: 'Planned', notes: '' });
  };

  const updatePlanItem = (id, patch) => {
    const treatmentPlan = (chart.treatmentPlan || []).map((row) => (
      row.id === id ? { ...row, ...patch } : row
    ));
    commit({ ...chart, treatmentPlan });
  };

  const removePlanItem = (id) => {
    commit({ ...chart, treatmentPlan: (chart.treatmentPlan || []).filter((r) => r.id !== id) });
  };

  const syncPlanFromProcedures = () => {
    const existing = new Set((chart.treatmentPlan || []).map((p) => p.procedureId).filter(Boolean));
    const additions = chart.procedures
      .filter((p) => !existing.has(p.id))
      .map((p) => ({
        id: `TP-${Date.now()}-${p.id}`,
        name: p.name,
        teeth: p.teeth || [],
        fee: Number(p.fee) || feeForProcedure(p.name),
        visits: p.visits || 1,
        status: 'Planned',
        notes: p.notes || '',
        procedureId: p.id,
      }));
    if (!additions.length) return;
    commit({ ...chart, treatmentPlan: [...(chart.treatmentPlan || []), ...additions] });
  };

  const printChart = () => {
    const win = window.open('', '_blank', 'width=900,height=1000');
    if (!win) return;
    const e = escapeHtml;
    const toothRows = Object.entries(chart.teeth)
      .filter(([, v]) => v?.condition && v.condition !== 'Healthy')
      .map(([n, v]) => `<tr><td>${e(n)}</td><td>${e(v.condition)}</td><td>${e((v.surfaces || []).join(', ') || '—')}</td><td>${e(v.note || '—')}</td></tr>`)
      .join('');
    const findings = chart.findings.map((f) => (
      `<li><strong>Teeth ${e(f.teeth.join(', '))}</strong>${f.surfaces?.length ? ` [${e(f.surfaces.join(''))}]` : ''} — ${e(f.text)} <em>(${e(f.date)})</em></li>`
    )).join('');
    const procs = chart.procedures.map((p) => (
      `<li><strong>${e(p.name)}</strong> · Tooth ${e((p.teeth || []).join(', ') || '—')} · ₹${e(Number(p.fee) || feeForProcedure(p.name))} · Visits: ${e(p.visits)} · ${e(p.date)}${p.notes ? ` — ${e(p.notes)}` : ''}</li>`
    )).join('');
    const plan = (chart.treatmentPlan || []).map((p) => (
      `<tr><td>${e(p.name)}</td><td>${e((p.teeth || []).join(', ') || '—')}</td><td>₹${e(Number(p.fee) || 0)}</td><td>${e(p.status)}</td><td>${e(p.visits)}</td></tr>`
    )).join('');
    const planTotal = (chart.treatmentPlan || []).reduce((a, p) => a + (Number(p.fee) || 0), 0);
    win.document.write(`<!doctype html><html><head><title>Dental Chart</title>
      <style>
        body{font-family:Segoe UI,Arial,sans-serif;padding:24px;color:#0f172a}
        h1{font-size:20px;margin:0 0 8px} h2{font-size:15px;margin:18px 0 8px;color:#0d6e6a}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th,td{border:1px solid #e2e8f0;padding:6px 8px;text-align:left}
        th{background:#f8fafc}
        .arch{display:flex;gap:4px;flex-wrap:wrap;margin:8px 0}
        .t{width:28px;height:28px;border-radius:6px;border:1px solid #94a3b8;display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:700}
      </style></head><body>
      <h1>Dental Chart (FDI)</h1>
      <div class="arch">${UPPER.map((n) => {
        const c = chart.teeth[n]?.condition || 'Healthy';
        return `<span class="t" style="background:${COLOR[c] || '#e2e8f0'}">${n}</span>`;
      }).join('')}</div>
      <div class="arch">${LOWER.map((n) => {
        const c = chart.teeth[n]?.condition || 'Healthy';
        return `<span class="t" style="background:${COLOR[c] || '#e2e8f0'}">${n}</span>`;
      }).join('')}</div>
      <h2>Tooth status</h2>
      <table><thead><tr><th>Tooth</th><th>Condition</th><th>Surfaces</th><th>Note</th></tr></thead>
      <tbody>${toothRows || '<tr><td colspan="4">No abnormal findings marked</td></tr>'}</tbody></table>
      <h2>Oral findings / examination</h2>
      <ul>${findings || '<li>None</li>'}</ul>
      <h2>Dental procedures</h2>
      <ul>${procs || '<li>None</li>'}</ul>
      <h2>Treatment plan & fees</h2>
      <table><thead><tr><th>Procedure</th><th>Teeth</th><th>Fee</th><th>Status</th><th>Visits</th></tr></thead>
      <tbody>${plan || '<tr><td colspan="5">No treatment plan items</td></tr>'}</tbody>
      <tfoot><tr><td colspan="2"><strong>Total</strong></td><td colspan="3"><strong>₹${e(planTotal.toLocaleString('en-IN'))}</strong></td></tr></tfoot></table>
      <p style="margin-top:28px;font-size:11px;color:#64748b">Printed ${e(new Date().toLocaleString('en-IN'))} · ${e(hospitalName || 'Hospital')} HMS</p>
      </body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const Tooth = ({ num }) => {
    const c = chart.teeth[num]?.condition || 'Healthy';
    const active = selected.includes(num);
    return (
      <button
        type="button"
        className={`dental-tooth${active ? ' active' : ''}`}
        style={{
          background: COLOR[c] || '#e2e8f0',
          outline: active ? '2px solid #0d6e6a' : undefined,
          fontWeight: 700,
        }}
        title={`Tooth ${num}: ${c}`}
        onClick={() => toggleTooth(num)}
        disabled={readOnly}
      >
        {num}
      </button>
    );
  };

  return (
    <div className="dental-chart card" style={{ borderLeft: '4px solid #0d6e6a' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <h4 style={{ margin: 0, color: '#0d6e6a' }}>🦷 Dental Chart (FDI) — Dental OP</h4>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            className="input"
            style={{ width: 'auto' }}
            disabled={readOnly}
            value={chart.dentition}
            onChange={(e) => commit({ ...chart, dentition: e.target.value })}
          >
            <option value="adult">Adult dentition</option>
            <option value="mixed">Mixed dentition</option>
            <option value="pediatric">Pediatric (guidance)</option>
          </select>
          {!readOnly && (
            <button type="button" className={`btn btn-sm ${multi ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMulti((m) => !m)}>
              {multi ? 'Multi-select ON' : 'Select multiple teeth'}
            </button>
          )}
          <button type="button" className="btn btn-sm btn-secondary" onClick={printChart}>🖨 Print chart</button>
        </div>
      </div>

      <div className="dental-row upper" style={{ marginTop: 14 }}>{UPPER.map((n) => <Tooth key={n} num={n} />)}</div>
      <div className="dental-midline" />
      <div className="dental-row lower">{LOWER.map((n) => <Tooth key={n} num={n} />)}</div>

      {selected.length > 0 && !readOnly && (
        <div className="dental-edit" style={{ marginTop: 14, padding: 12, background: '#f8fafc', borderRadius: 10 }}>
          <strong>Selected: {selected.join(', ')}</strong>
          <div className="form-grid" style={{ marginTop: 10 }}>
            <div className="field full">
              <label>Condition</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {CONDITIONS.map((c) => (
                  <button key={c} type="button" className="btn btn-sm btn-secondary" style={{ background: COLOR[c] }} onClick={() => setToothCondition(c)}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="field full">
              <label>Area / surface</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SURFACES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`btn btn-sm ${surfaces.includes(s.id) ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => toggleSurface(s.id)}
                  >
                    {s.id} — {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="field full">
              <label>Oral finding / examination</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input type="date" className="input" style={{ width: 160 }} value={findingDate} onChange={(e) => setFindingDate(e.target.value)} />
                <input className="input" style={{ flex: 1, minWidth: 180 }} placeholder="Examination / past procedure note…" value={findingText} onChange={(e) => setFindingText(e.target.value)} />
                <button type="button" className="btn btn-primary btn-sm" onClick={addFinding}>Add finding</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!readOnly && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
          <h4 style={{ margin: '0 0 10px' }}>Dental procedures</h4>
          <div className="form-grid">
            <div className="field">
              <label>Procedure</label>
              <input
                className="input"
                list="hcen-dental-procedures"
                placeholder="Search e.g. Root canal"
                value={procForm.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setProcForm((f) => ({ ...f, name, fee: f.fee || String(feeForProcedure(name)) }));
                }}
              />
              <datalist id="hcen-dental-procedures">
                {PROCEDURES.map((p) => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div className="field">
              <label>Tooth numbers</label>
              <input
                className="input"
                placeholder={selected.length ? selected.join(', ') : 'e.g. 16, 17'}
                value={procForm.tooth}
                onChange={(e) => setProcForm((f) => ({ ...f, tooth: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Fee (₹)</label>
              <input className="input" type="number" min={0} value={procForm.fee} onChange={(e) => setProcForm((f) => ({ ...f, fee: e.target.value }))} placeholder={String(feeForProcedure(procForm.name))} />
            </div>
            <div className="field">
              <label>Visits</label>
              <input className="input" type="number" min={1} value={procForm.visits} onChange={(e) => setProcForm((f) => ({ ...f, visits: e.target.value }))} />
            </div>
            <div className="field">
              <label>Date performed</label>
              <input className="input" type="date" value={procForm.date} onChange={(e) => setProcForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="field full">
              <label>Notes</label>
              <input className="input" value={procForm.notes} onChange={(e) => setProcForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Optional clinical notes" />
            </div>
          </div>
          <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={addProcedure}>+ Add procedure</button>
        </div>
      )}

      {!readOnly && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <h4 style={{ margin: 0 }}>Treatment plan & fees</h4>
            {chart.procedures.length > 0 && (
              <button type="button" className="btn btn-sm btn-secondary" onClick={syncPlanFromProcedures}>
                Import from procedures
              </button>
            )}
          </div>
          <div className="form-grid">
            <div className="field">
              <label>Procedure</label>
              <input
                className="input"
                list="hcen-dental-procedures"
                value={planForm.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setPlanForm((f) => ({ ...f, name, fee: f.fee || String(feeForProcedure(name)) }));
                }}
                placeholder="e.g. RCT"
              />
            </div>
            <div className="field">
              <label>Teeth</label>
              <input className="input" value={planForm.teeth} onChange={(e) => setPlanForm((f) => ({ ...f, teeth: e.target.value }))} placeholder={selected.length ? selected.join(', ') : '16'} />
            </div>
            <div className="field">
              <label>Fee (₹)</label>
              <input className="input" type="number" min={0} value={planForm.fee} onChange={(e) => setPlanForm((f) => ({ ...f, fee: e.target.value }))} />
            </div>
            <div className="field">
              <label>Status</label>
              <select className="input" value={planForm.status} onChange={(e) => setPlanForm((f) => ({ ...f, status: e.target.value }))}>
                {PLAN_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="field full">
              <label>Notes</label>
              <input className="input" value={planForm.notes} onChange={(e) => setPlanForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Patient consent / staging notes" />
            </div>
          </div>
          <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={() => addPlanItem()}>+ Add to treatment plan</button>

          {(chart.treatmentPlan || []).length > 0 && (
            <div style={{ marginTop: 14, overflowX: 'auto' }}>
              <table className="data-table" style={{ fontSize: 13, width: '100%' }}>
                <thead>
                  <tr>
                    <th>Procedure</th>
                    <th>Teeth</th>
                    <th>Fee</th>
                    <th>Status</th>
                    <th>Visits</th>
                    {!readOnly && <th />}
                  </tr>
                </thead>
                <tbody>
                  {(chart.treatmentPlan || []).map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>{(row.teeth || []).join(', ') || '—'}</td>
                      <td>
                        {readOnly ? `₹${Number(row.fee) || 0}` : (
                          <input
                            className="input"
                            type="number"
                            min={0}
                            style={{ width: 90 }}
                            value={row.fee}
                            onChange={(e) => updatePlanItem(row.id, { fee: Number(e.target.value) || 0 })}
                          />
                        )}
                      </td>
                      <td>
                        {readOnly ? row.status : (
                          <select className="input" style={{ width: 120 }} value={row.status} onChange={(e) => updatePlanItem(row.id, { status: e.target.value })}>
                            {PLAN_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                      </td>
                      <td>{row.visits || 1}</td>
                      {!readOnly && (
                        <td>
                          <button type="button" className="btn btn-sm btn-secondary" onClick={() => removePlanItem(row.id)}>Remove</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 13, flexWrap: 'wrap' }}>
                <span><strong>Planned:</strong> ₹{planTotals.planned.toLocaleString('en-IN')}</span>
                <span><strong>Accepted:</strong> ₹{planTotals.accepted.toLocaleString('en-IN')}</span>
                <span><strong>Completed:</strong> ₹{planTotals.completed.toLocaleString('en-IN')}</span>
                <span style={{ fontWeight: 800, color: '#0d6e6a' }}><strong>Total:</strong> ₹{planTotals.grand.toLocaleString('en-IN')}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {(chart.findings.length > 0 || chart.procedures.length > 0) && (
        <div style={{ marginTop: 14, fontSize: 13 }}>
          {chart.findings.length > 0 && (
            <>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Findings</div>
              <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
                {chart.findings.map((f) => (
                  <li key={f.id}>Teeth {f.teeth.join(', ')}{f.surfaces?.length ? ` [${f.surfaces.join('')}]` : ''}: {f.text} ({f.date})</li>
                ))}
              </ul>
            </>
          )}
          {chart.procedures.length > 0 && (
            <>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Procedures</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {chart.procedures.map((p) => (
                  <li key={p.id}>
                    {p.name} · {(p.teeth || []).join(', ') || '—'} · ₹{Number(p.fee) || feeForProcedure(p.name)} · {p.visits} visit(s) · {p.date}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
